import { Link } from "react-router-dom";
import { Panel, PageHeader } from "@/components/admin-shell";
import { SeoAnalyzer } from "@/components/SeoAnalyzer";
import { RichTextEditor } from "@/components/RichTextEditor";
import { apiRequest } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Edit, Calendar, Trash2, X, Upload, Loader2, Eye, Search } from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";

interface IBlogPost {
  _id: string;
  slug: string;
  title: { bn: string; en: string };
  excerpt: { bn: string; en: string };
  content: { bn: string; en: string };
  category: { bn: string; en: string };
  cover: string;
  coverAlt?: string;
  date: string;
  featured: boolean;
  published: boolean;
  relatedProducts?: any[];
  metaTitle?: { bn: string; en: string };
  metaDescription?: { bn: string; en: string };
  faqs?: { question: { bn: string; en: string }; answer: { bn: string; en: string } }[];
  metaKeywords?: string;
  canonicalUrl?: string;
  socialImage?: string;
  robotsMeta?: {
    noindex?: boolean;
    nofollow?: boolean;
    noarchive?: boolean;
  };
}

interface IProductLight {
  _id: string;
  title: { bn: string; en: string };
  slug: string;
  cover: string;
}

export const BLOG_CATEGORIES = [
  { value: "heritage", en: "Heritage", bn: "ঐতিহ্য" },
  { value: "guide", en: "Fragrance Guide", bn: "সুগন্ধি গাইড" },
  { value: "tips", en: "Tips & Care", bn: "টিপস ও যত্ন" },
  { value: "bts", en: "Behind the Scenes", bn: "পর্দার আড়ালে" },
  { value: "spotlight", en: "Product Spotlight", bn: "প্রোডাক্ট স্পটলাইট" },
];

export default function BlogPage() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<IBlogPost | null>(null);

  // Form State
  const [titleEn, setTitleEn] = useState("");
  const [titleBn, setTitleBn] = useState("");
  const [slug, setSlug] = useState("");
  const [isSlugUserModified, setIsSlugUserModified] = useState(false);
  const [excerptEn, setExcerptEn] = useState("");
  const [excerptBn, setExcerptBn] = useState("");

  const slugify = (text: string) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleTitleEnChange = (val: string) => {
    setTitleEn(val);
    if (!isSlugUserModified && !editingPost) {
      setSlug(slugify(val));
    }
  };
  const [contentEn, setContentEn] = useState("");
  const [contentBn, setContentBn] = useState("");
  const [categoryVal, setCategoryVal] = useState("heritage");
  const [coverUrl, setCoverUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [isPublished, setIsPublished] = useState(true);
  const [publishDate, setPublishDate] = useState("");
  const [coverAlt, setCoverAlt] = useState("");
  const [relatedProductIds, setRelatedProductIds] = useState<string[]>([]);
  const [productSearch, setProductSearch] = useState("");

  // AEO/SEO States
  const [metaTitleEn, setMetaTitleEn] = useState("");
  const [metaTitleBn, setMetaTitleBn] = useState("");
  const [metaDescEn, setMetaDescEn] = useState("");
  const [metaDescBn, setMetaDescBn] = useState("");
  const [faqs, setFaqs] = useState<
    { question: { en: string; bn: string }; answer: { en: string; bn: string } }[]
  >([]);
  const [metaKeywords, setMetaKeywords] = useState("");
  const [canonicalUrl, setCanonicalUrl] = useState("");
  const [socialImage, setSocialImage] = useState("");
  const [robotsMeta, setRobotsMeta] = useState({
    noindex: false,
    nofollow: false,
    noarchive: false,
  });
  const [focusKeywords, setFocusKeywords] = useState("");

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);
  };

  const handleClearImage = () => {
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setImageFile(null);
    setPreviewUrl("");
    setCoverUrl("");
  };

  // Fetch Posts
  const {
    data: posts = [],
    isLoading,
    error,
  } = useQuery<IBlogPost[]>({
    queryKey: ["posts"],
    queryFn: () => apiRequest<IBlogPost[]>("/posts?isAdmin=true"),
  });

  // Fetch Products for Related Products selector (Module 9.6)
  const { data: allProducts = [] } = useQuery<IProductLight[]>({
    queryKey: ["products-light"],
    queryFn: () => apiRequest<IProductLight[]>("/products?limit=500&fields=title,slug,cover"),
  });

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return allProducts.slice(0, 20);
    const needle = productSearch.toLowerCase();
    return allProducts.filter(
      (p) =>
        p.title.en.toLowerCase().includes(needle) ||
        p.title.bn.includes(productSearch)
    ).slice(0, 20);
  }, [allProducts, productSearch]);

  // Create Post Mutation
  const createMutation = useMutation({
    mutationFn: (newPost: any) => apiRequest<IBlogPost>("/posts", "POST", newPost),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      toast.success("Blog post created successfully");
      setIsOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to create post");
    },
  });

  // Update Post Mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      apiRequest<IBlogPost>(`/posts/${id}`, "PATCH", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      toast.success("Blog post updated successfully");
      setIsOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to update post");
    },
  });

  // Delete Post Mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/posts/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      toast.success("Blog post deleted successfully");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to delete post");
    },
  });

  const resetForm = () => {
    setEditingPost(null);
    setTitleEn("");
    setTitleBn("");
    setSlug("");
    setIsSlugUserModified(false);
    setExcerptEn("");
    setExcerptBn("");
    setContentEn("");
    setContentBn("");
    setCategoryVal("heritage");
    setCoverUrl("");
    setImageFile(null);
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");
    setIsFeatured(false);
    setIsPublished(true);
    setPublishDate("");
    setCoverAlt("");
    setRelatedProductIds([]);
    setProductSearch("");
    setMetaTitleEn("");
    setMetaTitleBn("");
    setMetaDescEn("");
    setMetaDescBn("");
    setFaqs([]);
    setMetaKeywords("");
    setCanonicalUrl("");
    setSocialImage("");
    setRobotsMeta({ noindex: false, nofollow: false, noarchive: false });
    setFocusKeywords("");
  };

  const handleOpenEditModal = (post: IBlogPost) => {
    setEditingPost(post);
    setTitleEn(post.title.en);
    setTitleBn(post.title.bn);
    setSlug(post.slug || "");
    setIsSlugUserModified(true);
    setExcerptEn(post.excerpt.en);
    setExcerptBn(post.excerpt.bn);
    setContentEn(post.content.en);
    setContentBn(post.content.bn);

    const catValue = BLOG_CATEGORIES.find((c) => c.en === post.category.en)?.value || "heritage";
    setCategoryVal(catValue);

    setCoverUrl(post.cover);
    setCoverAlt(post.coverAlt || "");
    setPreviewUrl("");
    setIsFeatured(post.featured || false);
    setIsPublished(post.published || false);
    setPublishDate(post.date ? new Date(post.date).toISOString().slice(0, 16) : "");
    setRelatedProductIds((post.relatedProducts || []).map((p: any) => typeof p === 'string' ? p : p._id));
    setProductSearch("");

    setMetaTitleEn(post.metaTitle?.en || "");
    setMetaTitleBn(post.metaTitle?.bn || "");
    setMetaDescEn(post.metaDescription?.en || "");
    setMetaDescBn(post.metaDescription?.bn || "");
    setFaqs(post.faqs || []);
    setMetaKeywords(post.metaKeywords || "");
    setCanonicalUrl(post.canonicalUrl || "");
    setSocialImage(post.socialImage || "");
    setRobotsMeta({
      noindex: !!post.robotsMeta?.noindex,
      nofollow: !!post.robotsMeta?.nofollow,
      noarchive: !!post.robotsMeta?.noarchive,
    });
    setFocusKeywords(post.metaKeywords || "");

    setIsOpen(true);
  };

  const handleSocialUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const toastId = toast.loading("Uploading social share photo to Cloudinary...");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "subaashghor/blog");

    try {
      const response = await apiRequest<{ url: string }>("/uploads", "POST", formData);
      setSocialImage(response.url);
      toast.success("Social image uploaded successfully", { id: toastId });
    } catch (err: any) {
      toast.error(err.message || "Failed to upload image", { id: toastId });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !titleEn ||
      !titleBn ||
      !excerptEn ||
      !excerptBn ||
      !categoryVal ||
      !contentEn ||
      !contentBn ||
      (!coverUrl && !imageFile)
    ) {
      toast.error("Please fill in all required fields and select a cover image");
      return;
    }

    let finalCoverUrl = coverUrl;

    if (imageFile) {
      setIsUploading(true);
      const toastId = toast.loading("Uploading cover image to Cloudinary...");
      const formData = new FormData();
      formData.append("file", imageFile);
      formData.append("folder", "subaashghor/blog");

      try {
        const response = await apiRequest<{ url: string }>("/uploads", "POST", formData);
        finalCoverUrl = response.url;
        setCoverUrl(finalCoverUrl);
        toast.success("Cover image uploaded successfully", { id: toastId });
      } catch (err: any) {
        toast.error(err.message || "Failed to upload cover image", { id: toastId });
        setIsUploading(false);
        return;
      } finally {
        setIsUploading(false);
      }
    }

    const selectedCat = BLOG_CATEGORIES.find((c) => c.value === categoryVal) || BLOG_CATEGORIES[0];

    const payload: Record<string, any> = {
      slug: slugify(slug || titleEn || "blog-post"),
      title: { en: titleEn, bn: titleBn },
      excerpt: { en: excerptEn, bn: excerptBn },
      content: { en: contentEn, bn: contentBn },
      category: { en: selectedCat.en, bn: selectedCat.bn },
      cover: finalCoverUrl || "https://res.cloudinary.com/demo/image/upload/v1672322312/blog-1.jpg",
      coverAlt,
      featured: isFeatured,
      published: isPublished,
      relatedProducts: relatedProductIds,
      metaTitle: { en: metaTitleEn, bn: metaTitleBn },
      metaDescription: { en: metaDescEn, bn: metaDescBn },
      faqs: faqs.filter((faq) => faq.question.en.trim() !== "" || faq.question.bn.trim() !== ""),
      metaKeywords,
      canonicalUrl,
      socialImage,
      robotsMeta,
    };

    // Module 9.5: Scheduled Publishing — include custom date if set
    if (publishDate) {
      payload.date = new Date(publishDate).toISOString();
    }

    if (editingPost) {
      updateMutation.mutate({ id: editingPost._id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <>
      {!isOpen && (
        <>
          <PageHeader
            title="Blog Management"
            subtitle="Bilingual editorial content — stories, guides and brand voice."
            action={
              <button
                onClick={() => setIsOpen(true)}
                className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition cursor-pointer"
              >
                <Plus className="h-4 w-4" /> New post
              </button>
            }
          />

          {isLoading ? (
            <div className="flex h-64 items-center justify-center text-gold">Loading posts...</div>
          ) : error ? (
            <div className="text-red-400">Error loading posts: {(error as Error).message}</div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {posts.map((p) => (
                <Panel key={p._id} className="hover:border-gold/40 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em]">
                        <span
                          className={`rounded px-2 py-0.5 ${
                            p.published
                              ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
                              : "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                          }`}
                        >
                          {p.published ? "Published" : "Draft"}
                        </span>
                        {p.featured && (
                          <span className="rounded bg-gold/10 text-gold border border-gold/30 px-2 py-0.5">
                            Featured
                          </span>
                        )}
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {new Date(p.date).toLocaleDateString()}
                        </span>
                      </div>
                      <h3 className="mt-2 font-display text-xl text-foreground leading-snug hover:text-gold transition">
                        <Link to={`/blog/${p.slug}`}>{p.title.en}</Link>
                      </h3>
                      <div className="text-sm text-gold-soft hover:text-gold transition" lang="bn">
                        <Link to={`/blog/${p.slug}`}>{p.title.bn}</Link>
                      </div>
                      <div className="mt-1 font-mono text-[11px] text-muted-foreground">
                        /blog/{p.slug}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Link
                        to={`/blog/${p.slug}`}
                        className="rounded-md border border-gold/25 p-2 text-gold-soft hover:bg-gold/10 hover:text-gold transition cursor-pointer flex items-center justify-center"
                        title="View post details"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Link>
                      <button
                        onClick={() => handleOpenEditModal(p)}
                        className="rounded-md border border-gold/25 p-2 text-gold-soft hover:bg-gold/10 hover:text-gold transition cursor-pointer flex items-center justify-center"
                        title="Edit post"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              `Are you sure you want to delete the blog post "${p.title.en}"?`,
                            )
                          ) {
                            deleteMutation.mutate(p._id);
                          }
                        }}
                        className="rounded-md border border-gold/25 p-2 text-red-400 hover:bg-red-500/10 transition cursor-pointer"
                        title="Delete post"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="ornate-divider my-4" />

                  <div className="flex items-center justify-between text-xs">
                    <div className="text-muted-foreground">
                      Category: {p.category.en} ({p.category.bn})
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">SEO Status: Healthy</span>
                    </div>
                  </div>
                </Panel>
              ))}
            </div>
          )}
        </>
      )}

      {/* Add Blog Post Inline View */}
      {isOpen && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gold/15 pb-4 gap-4">
            <div>
              <h1 className="font-display text-3xl text-gold">
                {editingPost ? "Edit Blog Post" : "New Blog Post"}
              </h1>
              <p className="text-xs text-muted-foreground mt-1">
                {editingPost
                  ? "Modify details of the blog article."
                  : "Create a bilingual blog article or story."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="inline-flex items-center gap-2 rounded-md border border-gold/30 px-4 py-2 text-sm font-semibold text-gold hover:bg-accent/40 transition cursor-pointer"
            >
              ← Back to Blog
            </button>
          </div>

          <div className="w-full rounded-xl border border-gold/30 bg-brand-card p-6 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Title (English) *
                  </label>
                  <input
                    type="text"
                    required
                    value={titleEn}
                    onChange={(e) => handleTitleEnChange(e.target.value)}
                    placeholder="The Art of Attar"
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Title (Bengali) *
                  </label>
                  <input
                    type="text"
                    required
                    value={titleBn}
                    onChange={(e) => setTitleBn(e.target.value)}
                    placeholder="আতরের শিল্প"
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                </div>
              </div>

              {/* URL Slug (Permalink) */}
              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  URL Slug (Permalink / Link ID) *
                </label>
                <div className="flex items-center rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground focus-within:border-gold">
                  <span className="text-muted-foreground text-xs select-none pr-1 font-mono">/blog/</span>
                  <input
                    type="text"
                    required
                    value={slug}
                    onChange={(e) => {
                      setIsSlugUserModified(true);
                      setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""));
                    }}
                    placeholder="e.g. art-of-attar"
                    className="w-full bg-transparent text-sm text-foreground outline-none font-mono"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Public storefront URL: <span className="text-gold font-mono">https://subaashghor.com/blog/{slug || "your-custom-slug"}</span>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Excerpt (English) *
                  </label>
                  <textarea
                    required
                    rows={2}
                    value={excerptEn}
                    onChange={(e) => setExcerptEn(e.target.value)}
                    placeholder="Brief description..."
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Excerpt (Bengali) *
                  </label>
                  <textarea
                    required
                    rows={2}
                    value={excerptBn}
                    onChange={(e) => setExcerptBn(e.target.value)}
                    placeholder="সংক্ষিপ্ত বর্ণনা..."
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <RichTextEditor
                    label="Content (English) *"
                    value={contentEn}
                    onChange={setContentEn}
                    placeholder="Write your article in English here... Use the toolbar to add headings, bold text, lists, links, etc."
                  />
                </div>
                <div>
                  <RichTextEditor
                    label="Content (Bengali) *"
                    value={contentBn}
                    onChange={setContentBn}
                    placeholder="এখানে বাংলায় আপনার আর্টিকেলটি লিখুন..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-2">
                  Category *
                </label>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                  {BLOG_CATEGORIES.map((cat) => {
                    const isSelected = categoryVal === cat.value;
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setCategoryVal(cat.value)}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border text-center transition-all cursor-pointer ${
                          isSelected
                            ? "border-gold bg-gold/10 text-gold shadow-[0_0_12px_rgba(212,175,55,0.15)]"
                            : "border-gold/15 bg-accent/15 text-muted-foreground hover:border-gold/45 hover:text-foreground"
                        }`}
                      >
                        <span className="text-xs font-semibold tracking-wide">{cat.en}</span>
                        <span className="text-[10px] text-gold-soft/85 mt-0.5" lang="bn">
                          {cat.bn}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  Cover Image *
                </label>
                {previewUrl || coverUrl ? (
                  <div className="relative rounded-lg border border-gold/30 bg-accent/10 p-2 flex items-center gap-3">
                    <img
                      src={previewUrl || coverUrl}
                      alt="Preview"
                      className="h-16 w-16 rounded object-cover border border-gold/25"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-foreground truncate">
                        {imageFile ? imageFile.name : coverUrl || "Cover Image"}
                      </div>
                      <div className="text-[10px] text-gold-soft">
                        {coverUrl ? "Uploaded to Cloudinary" : "Pending save (Local preview)"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleClearImage}
                      className="rounded bg-red-500/10 border border-red-500/30 p-1.5 text-red-300 hover:bg-red-500/25 transition cursor-pointer"
                      title="Remove image"
                      disabled={isUploading}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="relative flex flex-col items-center justify-center border-2 border-dashed border-gold/30 rounded-lg p-6 bg-accent/5 hover:bg-accent/10 transition">
                    <label className="flex flex-col items-center cursor-pointer">
                      <Upload className="h-8 w-8 text-gold-soft mb-2" />
                      <span className="text-xs text-foreground font-medium">
                        Click to select cover image
                      </span>
                      <span className="text-[10px] text-muted-foreground mt-1">
                        Supports JPG, PNG or WEBP (Max 5MB)
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageSelect}
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Module 9.4: Cover Alt Text */}
              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  Cover Image Alt Text (For Google Image SEO)
                </label>
                <input
                  type="text"
                  value={coverAlt}
                  onChange={(e) => setCoverAlt(e.target.value)}
                  placeholder="e.g. Premium Oud Attar bottle on wooden surface"
                  className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                />
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={isFeatured}
                    onChange={(e) => setIsFeatured(e.target.checked)}
                    className="rounded border-gold/30 bg-accent text-gold focus:ring-0"
                  />
                  Featured Article
                </label>

                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                    className="rounded border-gold/30 bg-accent text-gold focus:ring-0"
                  />
                  Publish Immediately
                </label>

                {/* Module 9.5: Scheduled Publishing */}
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    <Calendar className="inline h-3 w-3 mr-1" />
                    Publish Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={publishDate}
                    onChange={(e) => setPublishDate(e.target.value)}
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Leave empty to publish at current time. Set a future date to schedule.
                  </p>
                </div>
              </div>

              {/* Module 9.6: Related Products (Contextual Selling) */}
              <div className="border-t border-gold/15 pt-4">
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-2">
                  Related Products (Contextual Selling)
                </label>

                {/* Selected Products */}
                {relatedProductIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {relatedProductIds.map((id) => {
                      const product = allProducts.find((p) => p._id === id);
                      if (!product) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-gold/5 px-3 py-1 text-xs text-foreground"
                        >
                          {product.title.en}
                          <button
                            type="button"
                            onClick={() => setRelatedProductIds(relatedProductIds.filter((i) => i !== id))}
                            className="text-red-400 hover:text-red-300 cursor-pointer"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Product Search */}
                <div className="relative">
                  <label className="flex items-center gap-2 bg-accent/15 border border-gold/20 rounded-md px-3 py-2">
                    <Search className="h-3.5 w-3.5 text-gold-soft" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Search products to link..."
                      className="bg-transparent flex-1 text-sm outline-none text-foreground"
                    />
                  </label>
                  {productSearch.trim() && filteredProducts.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-gold/30 bg-brand-card shadow-xl">
                      {filteredProducts
                        .filter((p) => !relatedProductIds.includes(p._id))
                        .map((p) => (
                          <button
                            key={p._id}
                            type="button"
                            onClick={() => {
                              setRelatedProductIds([...relatedProductIds, p._id]);
                              setProductSearch("");
                            }}
                            className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-gold/10 transition cursor-pointer"
                          >
                            <img
                              src={p.cover}
                              alt={p.title.en}
                              className="h-8 w-8 rounded object-cover border border-gold/20"
                            />
                            <div className="min-w-0">
                              <div className="text-xs text-foreground truncate">{p.title.en}</div>
                              <div className="text-[10px] text-gold-soft" lang="bn">{p.title.bn}</div>
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  These products will appear at the bottom of the blog article as recommendations.
                </p>
              </div>

              {/* AEO/SEO Section (Module 08) */}
              <div className="border-t border-gold/15 pt-6 space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-gold">
                  SEO & AEO Intelligence (Bilingual Schema)
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left columns (2/3 width on large screens) */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Meta Title */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Meta Title (English)
                        </label>
                        <input
                          type="text"
                          value={metaTitleEn}
                          onChange={(e) => setMetaTitleEn(e.target.value)}
                          placeholder="Defaults to article title"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Meta Title (Bengali)
                        </label>
                        <input
                          type="text"
                          value={metaTitleBn}
                          onChange={(e) => setMetaTitleBn(e.target.value)}
                          placeholder="Defaults to article title"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                    </div>

                    {/* Meta Description */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Meta Description (English)
                        </label>
                        <textarea
                          rows={3}
                          value={metaDescEn}
                          onChange={(e) => setMetaDescEn(e.target.value)}
                          placeholder="Defaults to excerpt"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Meta Description (Bengali)
                        </label>
                        <textarea
                          rows={3}
                          value={metaDescBn}
                          onChange={(e) => setMetaDescBn(e.target.value)}
                          placeholder="Defaults to excerpt"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                    </div>

                    {/* Focus Keywords & Canonical URL */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Focus Keywords (Up to 5, comma-separated)
                        </label>
                        <input
                          type="text"
                          value={focusKeywords}
                          onChange={(e) => {
                            setFocusKeywords(e.target.value);
                            setMetaKeywords(e.target.value);
                          }}
                          placeholder="e.g. oud, care, guide"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Canonical URL
                        </label>
                        <input
                          type="text"
                          value={canonicalUrl}
                          onChange={(e) => setCanonicalUrl(e.target.value)}
                          placeholder="e.g. https://subaashghor.com/blog/oud-perfume-care"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                    </div>

                    {/* Social Image & Robots Meta */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Social Image (OG Image Override)
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={socialImage}
                            onChange={(e) => setSocialImage(e.target.value)}
                            placeholder="Image URL"
                            className="flex-1 min-w-0 rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                          />
                          <label className="flex items-center gap-2 rounded border border-dashed border-gold/30 bg-accent/15 px-3 py-2 text-xs text-gold cursor-pointer hover:border-gold hover:text-gold transition shrink-0">
                            <Upload className="h-4 w-4" />
                            <span>Upload</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={handleSocialUpload}
                            />
                          </label>
                        </div>
                        {socialImage && (
                          <img
                            src={socialImage}
                            alt="Social OG Override"
                            className="mt-2 h-16 w-28 rounded object-cover border border-gold/20 animate-fade-in"
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-2">
                          Robots Meta Settings
                        </label>
                        <div className="space-y-2 border border-gold/15 bg-accent/5 p-3 rounded">
                          <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={robotsMeta.noindex}
                              onChange={(e) =>
                                setRobotsMeta({ ...robotsMeta, noindex: e.target.checked })
                              }
                              className="rounded border-gold/25 text-gold focus:ring-gold bg-accent/20 h-4 w-4"
                            />
                            <span>Noindex (Do not display this page in search results)</span>
                          </label>
                          <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={robotsMeta.nofollow}
                              onChange={(e) =>
                                setRobotsMeta({ ...robotsMeta, nofollow: e.target.checked })
                              }
                              className="rounded border-gold/25 text-gold focus:ring-gold bg-accent/20 h-4 w-4"
                            />
                            <span>Nofollow (Do not follow links on this page)</span>
                          </label>
                          <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={robotsMeta.noarchive}
                              onChange={(e) =>
                                setRobotsMeta({ ...robotsMeta, noarchive: e.target.checked })
                              }
                              className="rounded border-gold/25 text-gold focus:ring-gold bg-accent/20 h-4 w-4"
                            />
                            <span>Noarchive (Do not show cached link in search results)</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* FAQs */}
                    <div className="border-t border-gold/10 pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <label className="block text-xs uppercase tracking-wider text-gold-soft">
                          Post-Specific FAQs & FAQPage Schema
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setFaqs([
                              ...faqs,
                              { question: { en: "", bn: "" }, answer: { en: "", bn: "" } },
                            ])
                          }
                          className="text-xs text-gold hover:underline flex items-center gap-1 cursor-pointer"
                        >
                          <Plus className="h-3.5 w-3.5" /> Add FAQ
                        </button>
                      </div>

                      {faqs.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">
                          No custom FAQs defined. Blog fallback FAQs will be used.
                        </p>
                      ) : (
                        <div className="space-y-4 max-h-[160px] overflow-y-auto pr-2 border border-gold/10 p-3 rounded-lg bg-accent/5">
                          {faqs.map((faq, idx) => (
                            <div
                              key={idx}
                              className="border-b border-gold/10 pb-4 last:border-0 last:pb-0 space-y-3 relative pr-8"
                            >
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                                    Question (English)
                                  </label>
                                  <input
                                    type="text"
                                    required
                                    value={faq.question.en}
                                    onChange={(e) => {
                                      const newFaqs = [...faqs];
                                      newFaqs[idx].question.en = e.target.value;
                                      setFaqs(newFaqs);
                                    }}
                                    className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none focus:border-gold"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                                    Question (Bengali)
                                  </label>
                                  <input
                                    type="text"
                                    required
                                    value={faq.question.bn}
                                    onChange={(e) => {
                                      const newFaqs = [...faqs];
                                      newFaqs[idx].question.bn = e.target.value;
                                      setFaqs(newFaqs);
                                    }}
                                    className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none focus:border-gold"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                                    Answer (English)
                                  </label>
                                  <textarea
                                    rows={2}
                                    required
                                    value={faq.answer.en}
                                    onChange={(e) => {
                                      const newFaqs = [...faqs];
                                      newFaqs[idx].answer.en = e.target.value;
                                      setFaqs(newFaqs);
                                    }}
                                    className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none focus:border-gold"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                                    Answer (Bengali)
                                  </label>
                                  <textarea
                                    rows={2}
                                    required
                                    value={faq.answer.bn}
                                    onChange={(e) => {
                                      const newFaqs = [...faqs];
                                      newFaqs[idx].answer.bn = e.target.value;
                                      setFaqs(newFaqs);
                                    }}
                                    className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none focus:border-gold"
                                  />
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => setFaqs(faqs.filter((_, i) => i !== idx))}
                                className="absolute right-0 top-1/2 -translate-y-1/2 text-red-400 hover:bg-red-500/10 p-1 rounded cursor-pointer"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right column: SEO Realtime Analyzer */}
                  <div className="border border-gold/15 bg-accent/5 p-4 rounded-lg h-fit space-y-4">
                    <SeoAnalyzer
                      title={metaTitleEn || titleEn}
                      description={metaDescEn || excerptEn}
                      content={contentEn}
                      focusKeywords={focusKeywords}
                      canonicalUrl={canonicalUrl}
                      slug={
                        editingPost?.slug ||
                        titleEn
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")
                          .replace(/(^-|-$)/g, "")
                      }
                      folderPath="blog"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  disabled={isUploading || createMutation.isPending || updateMutation.isPending}
                  className="rounded border border-gold/30 px-4 py-2 text-sm text-gold hover:bg-accent cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUploading || createMutation.isPending || updateMutation.isPending}
                  className="rounded bg-gold px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50 cursor-pointer"
                >
                  {isUploading ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading Image...
                    </span>
                  ) : createMutation.isPending || updateMutation.isPending ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving Post...
                    </span>
                  ) : editingPost ? (
                    "Save Changes"
                  ) : (
                    "Save Post"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
